import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { getIcon, hexToRgb } from "@/lib/registry";

export default function ProfileCard({ profile, onClick, index = 0, isSelected = false }) {
  const Icon = getIcon(profile.icon);
  const rgb = hexToRgb(profile.color);

  return (
    <motion.button
      type="button"
      data-testid={`profile-card-${profile.id}`}
      onClick={() => onClick(profile)}
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{
        opacity: 1,
        y: isSelected ? -10 : 0,
        scale: isSelected ? 1.12 : 1,
      }}
      transition={
        isSelected
          ? { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
          : { duration: 0.55, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }
      }
      whileHover={!isSelected ? { scale: 1.06, y: -6 } : undefined}
      whileTap={!isSelected ? { scale: 0.97 } : undefined}
      style={{
        "--p-color": profile.color,
        "--p-rgb": rgb,
        boxShadow: isSelected
          ? `0 0 70px -6px rgba(${rgb}, 0.8), 0 0 140px -20px rgba(${rgb}, 0.4), 0 20px 60px rgba(0,0,0,0.5)`
          : undefined,
        backgroundColor: isSelected ? "var(--mh-card-hover)" : "var(--mh-card)",
        borderColor: isSelected ? profile.color : "var(--mh-border)",
      }}
      className={`group relative flex flex-col items-center justify-center p-6 md:p-8
                 rounded-3xl border overflow-hidden
                 w-36 h-36 md:w-44 md:h-44 lg:w-48 lg:h-48
                 transition-[background-color,border-color,box-shadow] duration-300 ease-out
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--p-color)]
                 ${!isSelected ? "hover:border-[var(--p-color)] hover:shadow-[0_0_60px_-8px_rgba(var(--p-rgb),0.6)]" : ""}`}
    >
      {/* Hover background fill */}
      {!isSelected && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ backgroundColor: "var(--mh-card-hover)" }}
        />
      )}

      {/* Shine sweep on hover */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{
          background: "linear-gradient(115deg, transparent 25%, rgba(255,255,255,0.07) 50%, transparent 75%)",
          backgroundSize: "200% 100%",
          animation: "none",
        }}
      />

      {/* Accent glow top-left corner */}
      <span
        aria-hidden
        className="absolute -top-8 -left-8 w-24 h-24 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-400"
        style={{ background: `radial-gradient(circle, rgba(${rgb},1) 0%, transparent 70%)`, filter: "blur(16px)" }}
      />

      {/* Icon circle */}
      <div
        className="relative w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-3.5 transition-all duration-300"
        style={{
          backgroundColor: isSelected ? profile.color : `rgba(${rgb}, 0.12)`,
          border: `1.5px solid ${isSelected ? "transparent" : `rgba(${rgb}, 0.25)`}`,
          boxShadow: isSelected ? `0 0 24px rgba(${rgb}, 0.5)` : undefined,
        }}
      >
        {/* Icon bg fill on hover */}
        {!isSelected && (
          <span
            aria-hidden
            className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{ backgroundColor: profile.color }}
          />
        )}
        <Icon
          className={`relative w-7 h-7 md:w-8 md:h-8 transition-colors duration-300
                     ${isSelected ? "text-white" : "text-white/60 group-hover:text-white"}`}
          style={{ color: !isSelected ? profile.color : undefined }}
          strokeWidth={1.5}
        />
      </div>

      <span
        data-testid={`profile-name-${profile.id}`}
        className={`relative text-sm md:text-base font-semibold transition-colors duration-300 truncate max-w-full
                   ${isSelected ? "text-white" : "text-white/75 group-hover:text-white"}`}
        style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.01em" }}
      >
        {profile.name}
      </span>

      {/* Badges */}
      <div className="relative flex items-center gap-1.5 mt-1.5">
        {profile.theme === "kids" && (
          <span
            className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
            style={{ background: "rgba(255,180,0,0.18)", color: "#FFB400", border: "1px solid rgba(255,180,0,0.3)" }}
          >
            Kids
          </span>
        )}
        {profile.hasPasscode && (
          <Lock className="w-3 h-3 text-white/25" strokeWidth={2} />
        )}
      </div>
    </motion.button>
  );
}
